/**
 * Student-level teacher notes (v3.96.0) — a running list of short, dated
 * notes about one student, kept on the student record and shown in the
 * student pop-up (opened from the Dashboard and from Letter/Sound, NWF and
 * PA 4K Results).
 * Kemal's decisions, 2026-11-01: every grade (for a 4K student the 4K
 * Readiness note is shown alongside, read-only); stored on the student
 * record; dated entries, newest first, each with who wrote it; a teacher can
 * delete their own entry; not for families — never on the family
 * report, with a hint on screen.
 *
 * Storage: `students/{id}.teacherNoteEntries` — an array of
 * { id, text, createdAt (ISO string), authorUid, authorName }. Added with
 * arrayUnion and removed with arrayRemove, so two teachers adding at once
 * never overwrite each other. Student records are per school year, so the
 * list starts fresh each year. Rules: the field joins the teacher-comments
 * trio on the Clever fence's allow-list (firestore.rules, students update);
 * reads are unchanged (whoever can read the student record).
 *
 * Where it never goes: the family report (the pop-up's section is `.no-print`;
 * v3.98.0: only the Student Report's staff copy prints notes); the
 * AI report-card writer (it takes only `grade` from the student record —
 * functions/src/ai/deidentify.ts); NextPath (named student fields only);
 * roster sync (merge writes of roster fields leave it alone).
 */

export const STUDENT_NOTES_FIELD = 'teacherNoteEntries';
// Same value as BOY_NOTES_TYPE (src/constants/boyItems.js); kept here so this
// module stays dependency-free. studentNotes.test.js pins that they agree.
export const READINESS_NOTES_TYPE = 'boy_notes';
export const STUDENT_NOTE_MAX = 500;

const isEntry = (e) => !!e && typeof e === 'object' && typeof e.id === 'string' && typeof e.text === 'string' && e.text.trim() !== '';

/** The student's note entries, newest first; tolerant of a missing or malformed field. */
export function studentNotesOf(studentDoc) {
  const raw = studentDoc && Array.isArray(studentDoc[STUDENT_NOTES_FIELD]) ? studentDoc[STUDENT_NOTES_FIELD] : [];
  return raw.filter(isEntry).slice().sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')));
}

/**
 * The 4K Readiness note per student (the v3.46+ notes doc, testType
 * 'boy_notes') from an `assessments` fetch a Results page already made —
 * those pages keep only their own test types, the student pop-up shows this.
 * @returns {Object<string,string>} studentId → note
 */
export function readinessNotesFromDocs(docs) {
  const out = {};
  (docs || []).forEach((d) => {
    const data = d && typeof d.data === 'function' ? d.data() : d;
    if (data && data.testType === READINESS_NOTES_TYPE && data.studentId && typeof data.note === 'string' && data.note.trim()) {
      out[data.studentId] = data.note.trim();
    }
  });
  return out;
}

/** What gets saved: trimmed, at most STUDENT_NOTE_MAX characters. */
export function cleanStudentNote(text) {
  return typeof text === 'string' ? text.trim().slice(0, STUDENT_NOTE_MAX) : '';
}

/** The name shown on an entry: the profile's display name (as the app header), else the sign-in's, else the e-mail's local part. */
export function noteAuthorName(user) {
  const profileName = user && user.profile && typeof user.profile.displayName === 'string' ? user.profile.displayName.trim() : '';
  if (profileName) return profileName;
  if (user && typeof user.displayName === 'string' && user.displayName.trim()) return user.displayName.trim();
  return user && typeof user.email === 'string' ? user.email.split('@')[0] : '';
}

/**
 * A new entry. `id` and `now` are injectable for tests. The id is random and
 * opaque (never derived from the student or the text).
 */
export function makeStudentNote(text, user, now = new Date(), id = randomId()) {
  return {
    id,
    text: cleanStudentNote(text),
    createdAt: now.toISOString(),
    authorUid: (user && user.uid) || '',
    authorName: noteAuthorName(user),
  };
}

/** Delete is offered on your own entries (and to admins). The rules do not tell entries apart — this is the app's rule. */
export function canDeleteStudentNote(entry, user) {
  if (!entry || !user) return false;
  if (user.role && user.role.admin) return true;
  return !!entry.authorUid && entry.authorUid === user.uid;
}

/** "Oct 30, 2026" for an entry's ISO date ('' when unreadable). */
export function formatNoteDate(iso) {
  const d = new Date(iso);
  if (!iso || Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function randomId() {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch (e) { /* fall through */ }
  return `n${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
}
